import { Injectable } from '@angular/core';
import { ICompanyResponse } from '@bca-company-data/models/company-response';
import * as companyDataActions from '@bca-company-data/store/company-data.actions';
import { IStation } from '@bca-compose/models/station';
import * as composeActions from '@bca-compose/store/compose.actions';
import { ApiService } from '@bca-core/services/api.service';
import { MessageType } from '@bca-shared/models/_enums';
import { ICompany } from '@bca-shared/models/company';
import { ISuggestion } from '@bca-shared/modules/type-ahead/type-ahead.models';
import * as appActions from '@bca-store/app.actions';
import { IAction } from '@bca-store/models/action';
import { createAction } from '@bca-store/models/action-creator';
import { IRootState } from '@bca-store/store.reducers';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AutoSuggestService {
  constructor(private _store$: Store<IRootState>, private _apiService: ApiService) {}

  public searchCompany(searchTerm$: Observable<string>): Observable<ISuggestion<ICompany>[]> {
    return searchTerm$.pipe(
      debounceTime(200),
      switchMap(searchTerm => {
        this._store$.dispatch(
          this._createAction(new companyDataActions.CompanyLoading({ loading: true }))
        );
        return this._apiService.searchCompany(searchTerm).pipe(
          catchError(error => {
            const { production } = environment;
            const message = {
              body: 'NOTIFICATION.ERROR.COMPANY_KVK',
              type: MessageType.ERROR,
              context: { error },
              originalException: error
            };
            this._store$.dispatch(this._createAction(new appActions.SetMessage(message)));
            return EMPTY;
          })
        );
      }),
      filter((response: ICompanyResponse) => response.success),
      map((response: ICompanyResponse) => response.bedrijfsGegevens),
      map(companies => {
        this._store$.dispatch(
          this._createAction(new companyDataActions.CompanyLoading({ loading: false }))
        );
        return companies.slice(0, 7).map(company => {
          const { name, kvkNumber } = company;
          const length = 55;
          return {
            subject: company,
            displayText:
              name.length > length
                ? `${name.substring(0, length)}... (${kvkNumber})`
                : `${name} (${kvkNumber})`
          } as ISuggestion<ICompany>;
        });
      })
    );
  }

  public searchStation(searchTerm$: Observable<string>): Observable<ISuggestion<IStation>[]> {
    return searchTerm$.pipe(
      debounceTime(150),
      switchMap(searchTerm => {
        this._store$.dispatch(
          this._createAction(new composeActions.StationsLoading({ loading: true }))
        );
        return this._apiService.searchStation(searchTerm).pipe(
          catchError(error => {
            const message = {
              body: 'NOTIFICATION.ERROR.STATION',
              type: MessageType.ERROR,
              context: { error },
              originalException: error
            };
            this._store$.dispatch(this._createAction(new appActions.SetMessage(message)));
            return EMPTY;
          })
        );
      }),
      map(stations => {
        this._store$.dispatch(
          this._createAction(new composeActions.StationsLoading({ loading: false }))
        );
        return stations.slice(0, 7).map(
          station =>
            ({
              subject: station,
              displayText: `${station.naam}`
            } as ISuggestion<IStation>)
        );
      })
    );
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, AutoSuggestService);
  }
}
